/* eslint-disable react/prop-types */
/* eslint-disable no-console */
import React, { Component } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font
} from "@react-pdf/renderer";

import PoppinsRegular from "../../assets/fonts/Poppins-Regular.ttf";
import PoppinsBold from "../../assets/fonts/Poppins-Bold.ttf";
import OpenSansRegular from "../../assets/fonts/OpenSans-Regular.ttf";
import OpenSansBold from "../../assets/fonts/OpenSans-Bold.ttf";

import Logo from "../../assets/images/remidio@3x.png";
import { convertEpochToDate } from "../../utils/date";
import OpticalBiometryRef from "../../assets/images/myopia-report/optical-biometry-ref.png";
import KeratometryRef from "../../assets/images/myopia-report/keratometry-ref.png";
import RefractionRef from "../../assets/images/myopia-report/refraction-ref.png";
import NormalVision from "../../assets/images/myopia-report/normal-vision.png";
import MyopicVision from "../../assets/images/myopia-report/myopic-vision.png";
import HyperopicVision from "../../assets/images/myopia-report/hyperopic-vision.png";
import NormalEyeDiagram from "../../assets/images/myopia-report/normal-eye-diagram.png";
import MyopicEyeDiagram from "../../assets/images/myopia-report/myopic-eye-diagram.png";
import AxialLengthIllustration from "../../assets/images/myopia-report/axial-length-illustration.png";
import CornealShapeIllustration from "../../assets/images/myopia-report/corneal-shape-illustration.png";
import RetinalScanIllustration from "../../assets/images/myopia-report/retinal-scan-illustration.png";

class MyopiaPDF extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  // ---------- helpers ----------
  safe = (v, fallback = "") =>
    v === null || v === undefined || Number.isNaN(v) ? fallback : v;

  fmt = (v, digits = 2) => {
    if (v === null || v === undefined || v === "") return "";
    if (typeof v === "string") return v;
    const n = Number(v);
    if (Number.isNaN(n)) return "";
    return n.toFixed(digits);
  };

  calculateAge = dob => {
    if (!dob) return "";
    const age = Math.floor((new Date() - new Date(dob)) / 3.15576e10);
    return age;
  };

  // Calculate SEQ (Spherical Equivalent)
  calculateSEQ = (sphere, cylinder) => {
    const s = Number(sphere) || 0;
    const c = Number(cylinder) || 0;
    return (s + c / 2).toFixed(2);
  };

  // Keratometry K1/K2: same format as keratometry readings modal (mm / DS @ angle)
  formatKeratometryK1 = value => {
    if (value === null || value === undefined || value === "") return "-";
    if (
      typeof value === "string" &&
      value.includes("mm") &&
      value.includes("DS")
    ) {
      return value;
    }
    if (typeof value === "string") {
      const parts = value.split("/");
      if (parts.length >= 2) {
        const mm = parseFloat(parts[0]);
        const rest = parts[1];
        const match = rest.match(/([\d.]+)D@(\d+)°/);
        if (match && !Number.isNaN(mm)) {
          const ds = parseFloat(match[1]);
          const angle = parseFloat(match[2]);
          if (!Number.isNaN(ds) && !Number.isNaN(angle)) {
            return `${mm.toFixed(2)} mm /${ds.toFixed(2)} DS @${angle}°`;
          }
        }
      }
      const num = parseFloat(value);
      if (!Number.isNaN(num)) {
        return `${num.toFixed(2)} mm`;
      }
      return value;
    }
    const num = typeof value === "number" ? value : parseFloat(value);
    if (Number.isNaN(num)) return "-";
    return `${num.toFixed(2)} mm`;
  };

  formatKeratometryK2 = value => {
    return this.formatKeratometryK1(value);
  };

  // Keratometry AST: same format as keratometry readings modal (DC @ angle)
  formatKeratometryAST = value => {
    if (value === null || value === undefined || value === "") return "-";
    if (
      typeof value === "string" &&
      value.includes("DC") &&
      value.includes("@")
    ) {
      return value;
    }
    if (typeof value === "string") {
      const match = value.match(/([\d.-]+)D@(\d+)°/);
      if (match) {
        const dc = parseFloat(match[1]);
        const angle = parseFloat(match[2]);
        if (!Number.isNaN(dc) && !Number.isNaN(angle)) {
          return `${dc.toFixed(2)}DC @${angle}°`;
        }
      }
      const parts = value.split("/");
      if (parts.length >= 2) {
        const dc = parseFloat(parts[0]);
        const angle = parseFloat(parts[1]);
        if (!Number.isNaN(dc) && !Number.isNaN(angle)) {
          return `${dc.toFixed(2)}DC @${angle}°`;
        }
      }
      const num = parseFloat(value);
      if (!Number.isNaN(num)) {
        return `${num.toFixed(2)}DC`;
      }
      return value;
    }
    const num = typeof value === "number" ? value : parseFloat(value);
    if (Number.isNaN(num)) return "-";
    return `${num.toFixed(2)}DC`;
  };

  // Calculate image dimensions maintaining aspect ratio
  calculateImageSize = (originalWidth, originalHeight, maxWidth, maxHeight) => {
    const aspectRatio = originalWidth / originalHeight;
    let width = maxWidth;
    let height = maxWidth / aspectRatio;
    if (height > maxHeight) {
      height = maxHeight;
      width = maxHeight * aspectRatio;
    }
    return { width, height };
  };

  // ---------- render ----------
  render() {
    const styles = StyleSheet.create({
      page: {
        paddingTop: 18,
        paddingHorizontal: 24,
        paddingBottom: 12,
        fontFamily: "Poppins",
        fontSize: 10,
        color: "#000"
      },
      header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 10,
        height: 28
      },
      headerLeft: {
        flexDirection: "row",
        alignItems: "center"
      },
      orgLogoContainer: {
        maxWidth: 80,
        maxHeight: 32,
        marginRight: 8
      },
      orgLogo: {
        objectFit: "contain"
      },
      orgDetails: {
        flexDirection: "column"
      },
      orgName: {
        fontSize: 10,
        fontFamily: "Poppins",
        fontWeight: "bold",
        color: "#262626"
      },
      siteName: {
        fontSize: 10,
        fontFamily: "Poppins",
        color: "#555"
      },
      headerRight: {
        fontSize: 12,
        fontStyle: "italic",
        fontFamily: "Poppins",
        fontWeight: "bold",
        color: "#262626"
      },
      titleBar: {
        backgroundColor: "#369d63",
        borderRadius: 4,
        paddingVertical: 4,
        marginBottom: 10,
        height: 20
      },
      titleText: {
        color: "#fff",
        fontSize: 12,
        fontFamily: "Poppins",
        fontWeight: "bold",
        textAlign: "center"
      },
      patientDetailsSection: {
        marginBottom: 6
      },
      patientDetailsHeaderRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 2
      },
      patientDetailsHeading: {
        fontSize: 12,
        fontFamily: "Poppins",
        fontWeight: "bold"
      },
      patientDetailsDateContainer: {
        flexDirection: "row",
        alignItems: "center"
      },
      patientDetailsSeparator: {
        height: 1,
        backgroundColor: "#000",
        marginBottom: 2
      },
      patientDetailsGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        marginBottom: 6
      },
      patientDetailItem: {
        width: "50%",
        flexDirection: "row",
        marginBottom: 2
      },
      patientDetailItemRight: {
        width: "50%",
        flexDirection: "row",
        marginBottom: 2,
        justifyContent: "flex-end",
        paddingLeft: 0
      },
      patientDetailLabel: {
        fontSize: 10,
        fontFamily: "Poppins",
        fontWeight: "bold",
        marginRight: 4
      },
      patientDetailValue: {
        fontSize: 10,
        fontFamily: "Poppins",
        color: "#555"
      },
      patientDetailValueRight: {
        fontSize: 10,
        fontFamily: "Poppins",
        color: "#555"
      },
      resultsCard: {
        borderTopWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderLeftWidth: 1,
        borderColor: "#6c6c6c",
        borderRadius: 8,
        marginBottom: 10,
        overflow: "hidden"
      },
      resultsHeader: {
        backgroundColor: "#369d63",
        height: 26,
        justifyContent: "center",
        paddingLeft: 16
      },
      resultsHeaderText: {
        color: "#fff",
        fontSize: 12,
        fontFamily: "Poppins",
        fontWeight: "bold"
      },
      tableHeader: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: "#cecece",
        minHeight: 20,
        borderBottomWidth: 1,
        borderBottomColor: "#c5c5c5"
      },
      tableHeaderCellW102: {
        padding: 3,
        fontSize: 10,
        fontWeight: 700,
        fontFamily: "Poppins",
        color: "#262626",
        backgroundColor: "#cecece",
        textAlign: "center",
        borderRightWidth: 1,
        borderRightColor: "#c5c5c5",
        borderBottomWidth: 1,
        borderBottomColor: "#c5c5c5",
        width: 102
      },
      tableHeaderCellW110: {
        padding: 3,
        fontSize: 10,
        fontWeight: 700,
        fontFamily: "Poppins",
        color: "#262626",
        backgroundColor: "#cecece",
        textAlign: "center",
        borderRightWidth: 1,
        borderRightColor: "#c5c5c5",
        borderBottomWidth: 1,
        borderBottomColor: "#c5c5c5",
        width: 110
      },
      // Results table: eye columns sized for worst-case keratometry (e.g. "12.34 mm /45.15 DS @180°")
      tableHeaderCellW145: {
        padding: 3,
        fontSize: 10,
        fontWeight: 700,
        fontFamily: "Poppins",
        color: "#262626",
        backgroundColor: "#cecece",
        textAlign: "center",
        borderRightWidth: 1,
        borderRightColor: "#c5c5c5",
        borderBottomWidth: 1,
        borderBottomColor: "#c5c5c5",
        width: 145
      },
      tableHeaderCellFlex1: {
        padding: 3,
        fontSize: 10,
        fontWeight: 700,
        fontFamily: "Poppins",
        color: "#262626",
        backgroundColor: "#cecece",
        textAlign: "center",
        borderRightWidth: 1,
        borderRightColor: "#c5c5c5",
        borderBottomWidth: 1,
        borderBottomColor: "#c5c5c5",
        flex: 1
      },
      tableRow: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#c5c5c5",
        minHeight: 18
      },
      tableCell: {
        padding: 3,
        fontSize: 10,
        fontFamily: "Poppins",
        textAlign: "center",
        borderRightWidth: 1,
        borderRightColor: "#c5c5c5",
        backgroundColor: "#ffffff"
      },
      tableCellLeft: {
        padding: 3,
        fontSize: 10,
        fontFamily: "Poppins",
        fontWeight: "bold",
        textAlign: "left",
        borderRightWidth: 1,
        borderRightColor: "#c5c5c5",
        backgroundColor: "#ffffff"
      },
      tableCellLast: {
        padding: 3,
        fontSize: 10,
        fontFamily: "Poppins",
        textAlign: "center",
        backgroundColor: "#ffffff"
      },
      sectionTitle: {
        fontSize: 9,
        fontStyle: "italic",
        fontFamily: "Poppins",
        fontWeight: "bold",
        padding: 3,
        backgroundColor: "#ffffff",
        textAlign: "center"
      },
      sectionTitleSpan: {
        width: 392, // 102 + 145 + 145: exact width for worst-case keratometry strings
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        backgroundColor: "#ffffff",
        minHeight: 18
      },
      sectionTitleRow: {
        flexDirection: "row",
        backgroundColor: "#ffffff",
        borderBottomWidth: 1,
        borderBottomColor: "#c5c5c5"
      },
      sectionTitleRightEye: {
        width: 145,
        borderRightWidth: 1,
        borderRightColor: "#c5c5c5",
        backgroundColor: "#ffffff"
      },
      sectionTitleLeftEye: {
        width: 145,
        backgroundColor: "#ffffff"
      },
      sectionWrapper: {
        flexDirection: "row",
        alignItems: "stretch",
        backgroundColor: "#ffffff"
      },
      sectionContentWrapper: {
        flexDirection: "column",
        width: 392, // 102 + 145 + 145: exact width for worst-case keratometry
        backgroundColor: "#ffffff"
      },
      referenceImageContainer: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        padding: 4,
        borderLeftWidth: 1,
        borderLeftColor: "#c5c5c5",
        borderRightWidth: 1,
        borderRightColor: "#c5c5c5",
        borderBottomWidth: 1,
        borderBottomColor: "#c5c5c5",
        backgroundColor: "#ffffff"
      },
      sectionRowWrapper: {
        flexDirection: "row",
        minHeight: 18,
        backgroundColor: "#ffffff",
        borderBottomWidth: 1,
        borderBottomColor: "#c5c5c5"
      },
      sectionDataRow: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#c5c5c5",
        minHeight: 18,
        backgroundColor: "#ffffff"
      },
      sectionLastRow: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#c5c5c5",
        minHeight: 18,
        backgroundColor: "#ffffff"
      },
      growthTrendCard: {
        borderWidth: 1,
        borderColor: "#6c6c6c",
        borderRadius: 8,
        marginBottom: 10,
        overflow: "hidden"
      },
      growthTrendHeader: {
        backgroundColor: "#369d63",
        height: 26,
        justifyContent: "center",
        paddingLeft: 16
      },
      growthTrendText: {
        fontSize: 8,
        fontFamily: "Poppins",
        color: "#191919",
        marginBottom: 6,
        paddingHorizontal: 16
      },
      graphContainer: {
        borderWidth: 1,
        borderColor: "#6c6c6c",
        borderRadius: 8,
        marginBottom: 6,
        overflow: "hidden"
      },
      graphHeader: {
        backgroundColor: "#369d63",
        height: 24,
        justifyContent: "center",
        paddingLeft: 12,
        marginBottom: 4
      },
      graphImage: {
        width: 220,
        height: 176,
        marginVertical: 3
      },
      graphContentWrapper: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        paddingHorizontal: 20,
        paddingVertical: 3
      },
      graphImageWrapper: {
        alignItems: "center"
      },
      graphLegend: {
        flexDirection: "column",
        paddingLeft: 16,
        justifyContent: "center"
      },
      legendItem: {
        flexDirection: "row",
        alignItems: "center",
        marginRight: 12,
        marginBottom: 4
      },
      legendColor: {
        width: 24,
        height: 10,
        marginRight: 8
      },
      legendText: {
        fontSize: 8,
        fontFamily: "Poppins",
        color: "#555"
      },
      bulletText: {
        fontSize: 8.5,
        fontFamily: "Poppins",
        color: "#191919",
        marginBottom: 4,
        paddingLeft: 12
      },
      disclaimer: {
        fontSize: 8.5,
        fontFamily: "Poppins",
        color: "#7f7f7f",
        marginBottom: 4,
        paddingHorizontal: 15
      },
      infoText: {
        fontSize: 8.5,
        fontFamily: "Poppins",
        color: "#7f7f7f",
        marginBottom: 1,
        paddingHorizontal: 15
      },
      footer: {
        position: "absolute",
        left: 24,
        right: 24,
        bottom: 15,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        height: 22
      },
      footerLogoContainer: {
        position: "absolute",
        left: 0,
        right: 0,
        alignItems: "center",
        justifyContent: "center"
      },
      footerPageNo: {
        position: "absolute",
        right: 0,
        alignItems: "flex-end",
        justifyContent: "center"
      },
      footLogo: {
        width: 58,
        height: 22
      },
      pageNoContainer: {
        backgroundColor: "#e3e3e3",
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 4,
        width: 55, // Width for "Page 1/3" text (8pt font, ~34pt text width) + 16pt padding
        justifyContent: "center",
        alignItems: "center"
      },
      pageNo: {
        fontSize: 8,
        fontFamily: "Poppins",
        fontWeight: 700,
        color: "#000000"
      },
      signatureCard: {
        borderWidth: 1,
        borderColor: "#6c6c6c",
        borderRadius: 8,
        marginBottom: 25, // Space for footer (22pt logo + 15pt bottom + 8pt margin)
        overflow: "hidden"
      },
      signatureHeader: {
        backgroundColor: "#369d63",
        height: 32,
        justifyContent: "center",
        paddingLeft: 16
      },
      signatureHeaderText: {
        color: "#fff",
        fontSize: 12,
        fontFamily: "Poppins",
        fontWeight: "bold"
      },
      signatureSection: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-end",
        paddingTop: 52,
        paddingBottom: 12,
        paddingHorizontal: 16
      },
      signatureLabel: {
        fontSize: 10,
        fontFamily: "Poppins",
        fontWeight: "bold",
        color: "#262626"
      },
      signatureLine: {
        width: 90,
        borderTopWidth: 0.5,
        borderTopColor: "#6c6c6c",
        marginBottom: 2
      },
      educationalSection: {
        borderWidth: 1,
        borderColor: "#6c6c6c",
        borderRadius: 8,
        padding: 8,
        marginBottom: 20 // Space for footer (22pt logo + 15pt bottom + 8pt margin)
      },
      educationalTitle: {
        fontSize: 11,
        fontWeight: 500, // Medium
        fontStyle: "italic",
        fontFamily: "Poppins",
        marginBottom: 3
      },
      educationalText: {
        fontSize: 9.5,
        fontFamily: "Poppins",
        marginBottom: 6,
        lineHeight: 1.3
      },
      imageRow: {
        flexDirection: "row",
        justifyContent: "space-around",
        marginBottom: 6
      },
      imageContainer: {
        alignItems: "center",
        width: 150
      },
      educationalImage: {
        width: 140,
        height: 140,
        marginBottom: 3
      },
      imageLabel: {
        fontSize: 9,
        fontFamily: "Poppins",
        fontWeight: "bold"
      },
      twoColumnLayout: {
        flexDirection: "row",
        alignItems: "stretch",
        marginTop: 6,
        gap: 8
      },
      column: {
        flex: 1,
        borderWidth: 1,
        borderColor: "#c5c5c5",
        borderRadius: 8,
        padding: 6,
        flexDirection: "column"
      },
      columnFillSpace: {
        flex: 1,
        minHeight: 0,
        paddingTop: 2
      },
      columnImage: {
        width: 114,
        height: 114,
        marginTop: 8,
        marginBottom: 12
      },
      columnImageContainer: {
        alignItems: "center",
        marginTop: 6,
        marginBottom: 6,
        marginHorizontal: 6
      },
      columnImageLarge: {
        width: 150,
        height: 150,
        marginBottom: 3
      },
      columnImageSmall: {
        width: 110,
        height: 72,
        marginRight: 6
      },
      columnImageSmallLarge: {
        width: 145,
        height: 95,
        marginRight: 8
      },
      imageWithText: {
        flexDirection: "row",
        marginBottom: 6,
        alignItems: "center"
      },
      imageWithTextTight: {
        flex: 1,
        flexDirection: "row",
        marginBottom: 0,
        alignItems: "center",
        minHeight: 0
      },
      imageTextContainer: {
        flex: 1
      },
      imageTextTitle: {
        fontSize: 8.5,
        fontFamily: "Poppins",
        fontWeight: "bold",
        marginBottom: 1
      },
      imageTextDesc: {
        fontSize: 8,
        fontFamily: "Poppins",
        color: "#555",
        lineHeight: 1.2
      }
    });

    // Register fonts AFTER StyleSheet.create() - exactly like PDF.js
    // Note: Register bold font with both normal and italic fontStyle since some styles use italic + bold
    Font.register({
      family: "Poppins",
      fonts: [
        { src: PoppinsRegular, fontWeight: 400 },
        { src: PoppinsBold, fontWeight: 700, fontStyle: "normal" },
        { src: PoppinsBold, fontWeight: 700, fontStyle: "italic" } // For italic + bold styles
      ]
    });

    Font.register({
      family: "OpenSans",
      fonts: [
        { src: OpenSansRegular },
        { src: OpenSansBold, fontWeight: 700, fontStyle: "normal" },
        { src: OpenSansBold, fontWeight: 700, fontStyle: "italic" } // For italic + bold styles
      ]
    });

    const {
      exam,
      biometryData,
      siteName,
      hasLogo,
      siteLogo,
      growthData = [],
      growthGraphImage,
      predictiveGraphImage,
      refractionType = "objective"
    } = this.props;

    const patientDetails = this.safe(exam?.patientDetails, {});
    const examDetails = this.safe(exam?.examDetails, {});
    const age = this.calculateAge(patientDetails.dateOfBirth);
    const gender = patientDetails.gender === "MALE" ? "M" : "F";

    const R = biometryData?.rightEye || null;
    const L = biometryData?.leftEye || null;

    // Resolve the refraction block (objective/subjective) chosen for this report.
    const RRefraction =
      refractionType === "subjective"
        ? R?.subjectiveRefractionData
        : R?.refractionData;
    const LRefraction =
      refractionType === "subjective"
        ? L?.subjectiveRefractionData
        : L?.refractionData;

    const orgName = this.safe(sessionStorage.getItem("orgName"), "");
    const userName = this.safe(sessionStorage.getItem("userName"), "");

    // Calculate SEQ for both eyes
    const seqRE =
      RRefraction?.s != null && RRefraction?.c != null
        ? this.calculateSEQ(RRefraction.s, RRefraction.c)
        : "";
    const seqLE =
      LRefraction?.s != null && LRefraction?.c != null
        ? this.calculateSEQ(LRefraction.s, LRefraction.c)
        : "";

    // ------------- small components -------------
    const Header = () => (
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          {hasLogo ? (
            <View style={styles.orgLogoContainer}>
              <Image src={siteLogo} style={styles.orgLogo} />
            </View>
          ) : (
            <View style={styles.orgLogoContainer} />
          )}
          <View style={styles.orgDetails}>
            <Text style={styles.orgName}>{orgName}</Text>
            <Text style={styles.siteName}>{this.safe(siteName)}</Text>
          </View>
        </View>
        <Text style={styles.headerRight}>Pristine OBM 12</Text>
      </View>
    );

    const PatientDetailsSection = () => (
      <View style={styles.patientDetailsSection}>
        <View style={styles.patientDetailsHeaderRow}>
          <Text style={styles.patientDetailsHeading}>PATIENT DETAILS</Text>
          <View style={styles.patientDetailsDateContainer}>
            <Text style={styles.patientDetailLabel}>Date:</Text>
            <Text style={styles.patientDetailValue}>
              {convertEpochToDate(examDetails.examDate || new Date().getTime())}
            </Text>
          </View>
        </View>
        <View style={styles.patientDetailsSeparator} />
        <View style={styles.patientDetailsGrid}>
          <View style={styles.patientDetailItem}>
            <Text style={styles.patientDetailLabel}>Name :</Text>
            <Text style={styles.patientDetailValue}>
              {this.safe(patientDetails.firstName, "")}{" "}
              {this.safe(patientDetails.lastName, "")}
            </Text>
          </View>
          <View style={styles.patientDetailItemRight}>
            <Text style={styles.patientDetailLabel}>Age/ Gender:</Text>
            <Text style={styles.patientDetailValueRight}>
              {age !== "" ? `${age} yrs/ ` : ""}
              {gender}
            </Text>
          </View>
          <View style={styles.patientDetailItem}>
            <Text style={styles.patientDetailLabel}>MRN:</Text>
            <Text style={styles.patientDetailValue}>
              {this.safe(patientDetails.mrn)}
            </Text>
          </View>
          <View style={styles.patientDetailItemRight}>
            <Text style={styles.patientDetailLabel}>
              Consultant&apos;s Name:
            </Text>
            <Text style={styles.patientDetailValueRight}>{userName}</Text>
          </View>
          <View style={styles.patientDetailItem}>
            <Text style={styles.patientDetailLabel}>Height:</Text>
            <Text style={styles.patientDetailValue}>
              {this.safe(examDetails.patientHeight, "")
                ? `${this.safe(examDetails.patientHeight, "")} cm`
                : ""}
            </Text>
          </View>
        </View>
      </View>
    );

    // ---------- PAGE 1 ----------
    const Page1 = () => (
      <Page size="A4" style={styles.page}>
        <Header />
        <View style={styles.titleBar}>
          <Text style={styles.titleText}>Optical Biometer Myopia Report</Text>
        </View>

        <PatientDetailsSection />

        {/* RESULTS Section */}
        <View style={styles.resultsCard}>
          <View style={styles.resultsHeader}>
            <Text style={styles.resultsHeaderText}>RESULTS</Text>
          </View>

          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={styles.tableHeaderCellW102}>Parameters</Text>
            <Text style={styles.tableHeaderCellW145}>Right Eye</Text>
            <Text style={styles.tableHeaderCellW145}>Left Eye</Text>
            <Text style={styles.tableHeaderCellFlex1}>
              Reference Illustrations
            </Text>
          </View>

          {/* Optical Biometry Section */}
          <View style={styles.sectionWrapper}>
            {/* Left side: Title + Data rows */}
            <View style={styles.sectionContentWrapper}>
              {/* Title row - normal height */}
              <View style={styles.sectionTitleRow}>
                <View style={[styles.sectionTitleSpan, styles.sectionTitle]}>
                  <Text style={styles.sectionTitle}>Optical Biometry</Text>
                </View>
              </View>

              {/* Data rows - only 3 columns (units: mm, none, DS) */}
              {[
                {
                  label: "Axial Length",
                  re: this.fmt(R?.axialData?.al, 2)
                    ? `${this.fmt(R?.axialData?.al, 2)} mm`
                    : "",
                  le: this.fmt(L?.axialData?.al, 2)
                    ? `${this.fmt(L?.axialData?.al, 2)} mm`
                    : ""
                },
                {
                  label: "AL/CR",
                  re: this.fmt(R?.alCr, 2),
                  le: this.fmt(L?.alCr, 2)
                },
                {
                  label: "SEQ",
                  re: seqRE ? `${seqRE} DS` : "",
                  le: seqLE ? `${seqLE} DS` : ""
                }
              ].map((row, idx) => {
                const isLast = idx === 2; // Last row in the section
                return (
                  <View
                    key={`biometry-${idx}`}
                    style={
                      isLast ? styles.sectionLastRow : styles.sectionDataRow
                    }
                  >
                    <Text style={[styles.tableCellLeft, { width: 102 }]}>
                      {row.label}
                    </Text>
                    <Text style={[styles.tableCell, { width: 145 }]}>
                      {row.re}
                    </Text>
                    <Text style={[styles.tableCellLast, { width: 145 }]}>
                      {row.le}
                    </Text>
                  </View>
                );
              })}
            </View>

            {/* Right side: Image spanning all 4 rows */}
            <View style={styles.referenceImageContainer}>
              <Image
                src={OpticalBiometryRef}
                style={{ width: 72, height: 72 }}
              />
            </View>
          </View>

          {/* Keratometry Section */}
          <View style={styles.sectionWrapper}>
            {/* Left side: Title + Data rows */}
            <View style={styles.sectionContentWrapper}>
              {/* Title row - normal height */}
              <View style={styles.sectionTitleRow}>
                <View style={[styles.sectionTitleSpan, styles.sectionTitle]}>
                  <Text style={styles.sectionTitle}>Keratometry</Text>
                </View>
              </View>

              {/* Data rows - K1/K2/AST formatted exactly as keratometry readings modal */}
              {[
                {
                  label: "K1",
                  re: this.formatKeratometryK1(R?.keratometryData?.k1),
                  le: this.formatKeratometryK1(L?.keratometryData?.k1)
                },
                {
                  label: "K2",
                  re: this.formatKeratometryK2(R?.keratometryData?.k2),
                  le: this.formatKeratometryK2(L?.keratometryData?.k2)
                },
                {
                  label: "AST",
                  re: this.formatKeratometryAST(R?.keratometryData?.ast),
                  le: this.formatKeratometryAST(L?.keratometryData?.ast)
                }
              ].map((row, idx) => {
                const isLast = idx === 2; // Last row in the section
                return (
                  <View
                    key={`keratometry-${idx}`}
                    style={
                      isLast ? styles.sectionLastRow : styles.sectionDataRow
                    }
                  >
                    <Text style={[styles.tableCellLeft, { width: 102 }]}>
                      {row.label}
                    </Text>
                    <Text style={[styles.tableCell, { width: 145 }]}>
                      {row.re}
                    </Text>
                    <Text style={[styles.tableCellLast, { width: 145 }]}>
                      {row.le}
                    </Text>
                  </View>
                );
              })}
            </View>

            {/* Right side: Image spanning all 4 rows */}
            <View style={styles.referenceImageContainer}>
              <Image src={KeratometryRef} style={{ width: 72, height: 72 }} />
            </View>
          </View>

          {/* Refraction Section */}
          <View style={styles.sectionWrapper}>
            {/* Left side: Title + Data rows */}
            <View style={styles.sectionContentWrapper}>
              {/* Title row - normal height */}
              <View style={styles.sectionTitleRow}>
                <View style={[styles.sectionTitleSpan, styles.sectionTitle]}>
                  <Text style={styles.sectionTitle}>
                    {refractionType === "subjective"
                      ? "Subjective Refraction"
                      : "Objective Refraction"}
                    {this.props.exam?.examDetails?.eyeState
                      ? ` (Eye State: ${this.props.exam.examDetails.eyeState})`
                      : ""}
                  </Text>
                </View>
              </View>

              {/* Data rows - only 3 columns (Sphere: DS, Cylinder: DC, Axis: °, 2 decimal places) */}
              {[
                {
                  label: "Sphere",
                  re: this.fmt(RRefraction?.s, 2)
                    ? `${this.fmt(RRefraction?.s, 2)} DS`
                    : "",
                  le: this.fmt(LRefraction?.s, 2)
                    ? `${this.fmt(LRefraction?.s, 2)} DS`
                    : ""
                },
                {
                  label: "Cylinder",
                  re: this.fmt(RRefraction?.c, 2)
                    ? `${this.fmt(RRefraction?.c, 2)} DC`
                    : "",
                  le: this.fmt(LRefraction?.c, 2)
                    ? `${this.fmt(LRefraction?.c, 2)} DC`
                    : ""
                },
                {
                  label: "Axis",
                  re: this.fmt(RRefraction?.a, 2)
                    ? `${this.fmt(RRefraction?.a, 2)} °`
                    : "",
                  le: this.fmt(LRefraction?.a, 2)
                    ? `${this.fmt(LRefraction?.a, 2)} °`
                    : ""
                }
              ].map((row, idx) => {
                const isLast = idx === 2; // Last row in the section
                return (
                  <View
                    key={`refraction-${idx}`}
                    style={
                      isLast ? styles.sectionLastRow : styles.sectionDataRow
                    }
                  >
                    <Text style={[styles.tableCellLeft, { width: 102 }]}>
                      {row.label}
                    </Text>
                    <Text style={[styles.tableCell, { width: 145 }]}>
                      {row.re}
                    </Text>
                    <Text style={[styles.tableCellLast, { width: 145 }]}>
                      {row.le}
                    </Text>
                  </View>
                );
              })}
            </View>

            {/* Right side: Image spanning all 4 rows */}
            <View style={styles.referenceImageContainer}>
              <Image src={RefractionRef} style={{ width: 72, height: 72 }} />
            </View>
          </View>
        </View>

        {/* Eye Growth Trend Section */}
        <View style={styles.growthTrendCard}>
          <View style={styles.growthTrendHeader}>
            <Text style={styles.resultsHeaderText}>EYE GROWTH TREND</Text>
          </View>
          <Text style={styles.growthTrendText}>
            Below is your child&apos;s eye growth trend. Staying within the
            healthy zone is our goal !
          </Text>

          {/* Growth Trend Table */}
          <View style={styles.tableHeader}>
            <Text style={styles.tableHeaderCellW102}>Session Date</Text>
            <Text style={styles.tableHeaderCellW110}>Axial Length RE (mm)</Text>
            <Text style={styles.tableHeaderCellW110}>Axial Length LE (mm)</Text>
            <Text style={styles.tableHeaderCellFlex1}>Indication/Remarks</Text>
          </View>

          {growthData.length > 0 ? (
            growthData.map((row, idx) => (
              <View key={`growth-${idx}`} style={styles.tableRow}>
                <Text style={[styles.tableCell, { width: 102 }]}>
                  {row.date}
                </Text>
                <Text style={[styles.tableCell, { width: 110 }]}>
                  {row.alRE != null && row.alRE !== ""
                    ? `${row.alRE} mm`
                    : row.alRE ?? ""}
                </Text>
                <Text style={[styles.tableCell, { width: 110 }]}>
                  {row.alLE != null && row.alLE !== ""
                    ? `${row.alLE} mm`
                    : row.alLE ?? ""}
                </Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>
                  {row.remarks || ""}
                </Text>
              </View>
            ))
          ) : (
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: 102 }]}>
                {convertEpochToDate(
                  examDetails.examDate || new Date().getTime()
                )}
              </Text>
              <Text style={[styles.tableCell, { width: 110 }]}>
                {(() => {
                  const val = this.fmt(R?.axialData?.al, 2);
                  return val != null && val !== "" ? `${val} mm` : val ?? "";
                })()}
              </Text>
              <Text style={[styles.tableCell, { width: 110 }]}>
                {(() => {
                  const val = this.fmt(L?.axialData?.al, 2);
                  return val != null && val !== "" ? `${val} mm` : val ?? "";
                })()}
              </Text>
              <Text style={[styles.tableCell, { flex: 1 }]} />
            </View>
          )}
        </View>

        <View style={styles.footer}>
          <View style={styles.footerLogoContainer}>
            <Image src={Logo} style={styles.footLogo} />
          </View>
          <View style={styles.footerPageNo}>
            <View style={styles.pageNoContainer}>
              <Text style={styles.pageNo}>Page 1/3</Text>
            </View>
          </View>
        </View>
      </Page>
    );

    // ---------- PAGE 2 ----------
    const Page2 = () => (
      <Page size="A4" style={styles.page}>
        <Header />

        {/* Growth Curve Section */}
        <View style={styles.graphContainer}>
          <View style={styles.graphHeader}>
            <Text style={styles.resultsHeaderText}>GROWTH CURVE</Text>
          </View>
          <View style={styles.graphContentWrapper}>
            <View style={styles.graphImageWrapper}>
              <Text style={{ fontSize: 8, color: "#555", marginBottom: 4 }}>
                Graph 1a
              </Text>
              {growthGraphImage ? (
                <Image src={growthGraphImage} style={styles.graphImage} />
              ) : (
                <View
                  style={[
                    styles.graphImage,
                    { backgroundColor: "#f0f0f0", justifyContent: "center" }
                  ]}
                >
                  <Text style={{ textAlign: "center", color: "#999" }}>
                    Growth Curve Graph
                  </Text>
                </View>
              )}
            </View>

            <View style={styles.graphLegend}>
              {[
                { label: "Real Patient Data", color: "#CBE8F8" },
                { label: "Emmetropic/Hyperopic", color: "#4C7B30" },
                { label: "Low-moderate Myopia", color: "#F1D549" },
                { label: "Enhanced Risk Myopia", color: "#ECAA56" },
                { label: "High Risk Myopia", color: "#E93322" },
                { label: "Extreme Myopia", color: "#B62618" }
              ].map((item, idx) => (
                <View key={`legend-${idx}`} style={styles.legendItem}>
                  <View
                    style={[
                      styles.legendColor,
                      { backgroundColor: item.color }
                    ]}
                  />
                  <Text style={styles.legendText}>{item.label}</Text>
                </View>
              ))}
            </View>
          </View>

          <View style={{ paddingHorizontal: 20, marginBottom: 4 }}>
            <Text style={styles.bulletText}>
              • The above graph depicts Myopic condition of an eye.
            </Text>
            <Text style={styles.bulletText}>
              • The various colors represent levels of Myopia, and the white
              indicators represents patient&apos;s level of Myopia.
            </Text>
          </View>
        </View>

        {/* Predictive Curve Section */}
        <View style={styles.graphContainer}>
          <View style={styles.graphHeader}>
            <Text style={styles.resultsHeaderText}>PREDICTIVE CURVE</Text>
          </View>
          <View style={styles.graphContentWrapper}>
            <View style={styles.graphImageWrapper}>
              <Text style={{ fontSize: 8, color: "#555", marginBottom: 4 }}>
                Graph 1b
              </Text>
              {predictiveGraphImage ? (
                <Image src={predictiveGraphImage} style={styles.graphImage} />
              ) : (
                <View
                  style={[
                    styles.graphImage,
                    { backgroundColor: "#f0f0f0", justifyContent: "center" }
                  ]}
                >
                  <Text style={{ textAlign: "center", color: "#999" }}>
                    Predictive Curve Graph
                  </Text>
                </View>
              )}
            </View>

            <View style={styles.graphLegend}>
              {(() => {
                const { selectedItems = {} } = this.props;
                const includeRealPatientData =
                  selectedItems.includeRealPatientData !== false; // Default to true
                const selectedTreatments = selectedItems.selectedTreatments || [
                  30,
                  40,
                  50,
                  60,
                  70,
                  0
                ]; // Default to all

                // Map mod values to treatment labels (order matches MyopiaPredictiveGraphModal)
                const allLegendItems = [
                  { label: "Real Patient Data", mod: "real", color: "#EC6C84" },
                  {
                    label: "Defocus Segment Spectacle lens",
                    mod: 70,
                    color: "#74140B"
                  },
                  {
                    label: "Anti-Myopia Orthokeratology",
                    mod: 40,
                    color: "#F1A253"
                  },
                  { label: "Atropine 0.025%", mod: 50, color: "#F6CE69" },
                  {
                    label: "Anti-Myopia soft contacts",
                    mod: 60,
                    color: "#6BBDBE"
                  },
                  { label: "Atropine 0.01%", mod: 30, color: "#569FE4" },
                  { label: "No treatment", mod: 0, color: "#C8CACE" }
                ];

                // Filter based on selectedItems
                const visibleItems = allLegendItems.filter(item => {
                  if (item.mod === "real") {
                    return includeRealPatientData;
                  }
                  return selectedTreatments.includes(item.mod);
                });

                return visibleItems.map((item, idx) => (
                  <View key={`legend2-${idx}`} style={styles.legendItem}>
                    <View
                      style={[
                        styles.legendColor,
                        { backgroundColor: item.color }
                      ]}
                    />
                    <Text style={styles.legendText}>{item.label}</Text>
                  </View>
                ));
              })()}
            </View>
          </View>

          <View style={{ paddingHorizontal: 20, marginBottom: 4 }}>
            <Text style={styles.bulletText}>
              • The above graph depicts progression of refractive error of the
              patient with respect to their age.
            </Text>
            <Text style={styles.bulletText}>
              • The pink curve shows refractive power; colors indicate predicted
              outcomes of different treatments for a more myopic eye.
            </Text>
          </View>
        </View>

        <Text style={styles.disclaimer}>
          Disclaimer: Graphs presented on this page are adapted from materials
          originally developed by MyAppia- The Myopia Progression calculated by
          Dr. Thomas Aller.
        </Text>

        <View style={styles.signatureCard}>
          <View style={styles.signatureHeader}>
            <Text style={styles.signatureHeaderText}>
              Consultant&apos;s Prescription
            </Text>
          </View>
          <View style={styles.signatureSection}>
            <Text style={styles.signatureLabel}>Next Appointment :</Text>
            <View style={{ alignItems: "flex-end" }}>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureLabel}>
                Consultant&apos;s Signature
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <View style={styles.footerLogoContainer}>
            <Image src={Logo} style={styles.footLogo} />
          </View>
          <View style={styles.footerPageNo}>
            <View style={styles.pageNoContainer}>
              <Text style={styles.pageNo}>Page 2/3</Text>
            </View>
          </View>
        </View>
      </Page>
    );

    // ---------- PAGE 3 ----------
    const Page3 = () => (
      <Page size="A4" style={styles.page}>
        <Header />

        <View style={styles.educationalSection}>
          <View style={styles.graphHeader}>
            <Text style={[styles.resultsHeaderText, { fontSize: 11 }]}>
              Significance of Myopia and its vitality in management
            </Text>
          </View>

          <Text style={styles.educationalTitle}>What is Myopia?</Text>
          <Text style={styles.educationalText}>
            Myopia, commonly known as nearsightedness, is an eye condition in
            which nearby objects appear clear,while distant objects may look
            blurred.
          </Text>

          {/* Vision Comparison Images */}
          <View style={styles.imageRow}>
            <View style={styles.imageContainer}>
              <Image src={NormalVision} style={styles.educationalImage} />
              <Text style={styles.imageLabel}>Normal Vision</Text>
            </View>
            <View style={styles.imageContainer}>
              <Image src={MyopicVision} style={styles.educationalImage} />
              <Text style={styles.imageLabel}>Myopic Vision</Text>
            </View>
            <View style={styles.imageContainer}>
              <Image src={HyperopicVision} style={styles.educationalImage} />
              <Text style={styles.imageLabel}>Hyperopic Vision</Text>
            </View>
          </View>

          {/* Two Column Layout */}
          <View style={styles.twoColumnLayout}>
            {/* Left Column */}
            <View style={styles.column}>
              <Text style={styles.educationalTitle}>Why does it happen?</Text>
              <Text style={styles.educationalText}>
                Myopia occurs when the eye is longer than normal, or the front
                part of the eye (cornea) is too curved. Because of this, light
                entering the eye focuses in front of the retina instead of
                directly on it, causing blurred distance vision.
              </Text>
              <View style={styles.columnImageContainer}>
                <Image src={NormalEyeDiagram} style={styles.columnImageLarge} />
                <Image
                  src={MyopicEyeDiagram}
                  style={[styles.columnImageLarge, { marginBottom: 0 }]}
                />
              </View>
            </View>

            {/* Right Column */}
            <View style={styles.column}>
              <Text style={styles.educationalTitle}>
                Can It change overtime?
              </Text>
              <Text style={[styles.educationalText, { marginBottom: 4 }]}>
                Myopia typically begins in childhood and may progress as the eye
                continues to grow. Early intervention is therefore essential,
                with regular monitoring of key markers such as:
              </Text>

              <View style={styles.columnFillSpace}>
                {/* Axial Length */}
                <View style={styles.imageWithTextTight}>
                  <Image
                    src={AxialLengthIllustration}
                    style={styles.columnImageSmallLarge}
                  />
                  <View style={styles.imageTextContainer}>
                    <Text style={styles.imageTextTitle}>Axial Length</Text>
                    <Text style={styles.imageTextDesc}>
                      Elongation of the eyeball.
                    </Text>
                  </View>
                </View>

                {/* Corneal Shape */}
                <View style={styles.imageWithTextTight}>
                  <Image
                    src={CornealShapeIllustration}
                    style={styles.columnImageSmallLarge}
                  />
                  <View style={styles.imageTextContainer}>
                    <Text style={styles.imageTextTitle}>Corneal Shape</Text>
                    <Text style={styles.imageTextDesc}>
                      The corneal curvature variations.
                    </Text>
                  </View>
                </View>

                {/* Retinal health */}
                <View style={styles.imageWithTextTight}>
                  <Image
                    src={RetinalScanIllustration}
                    style={styles.columnImageSmallLarge}
                  />
                  <View style={styles.imageTextContainer}>
                    <Text style={styles.imageTextTitle}>Retinal health</Text>
                    <Text style={styles.imageTextDesc}>
                      Fundus changes due to high myopia.
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <View style={styles.footerLogoContainer}>
            <Image src={Logo} style={styles.footLogo} />
          </View>
          <View style={styles.footerPageNo}>
            <View style={styles.pageNoContainer}>
              <Text style={styles.pageNo}>Page 3/3</Text>
            </View>
          </View>
        </View>
      </Page>
    );

    return (
      <Document>
        <Page1 />
        <Page2 />
        <Page3 />
      </Document>
    );
  }
}

export default MyopiaPDF;
